import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { serviceOrderService } from '../../services/serviceOrderService';
import { supplierService } from '../../services/supplierService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import TableWithMonthGroups from '../../components/Common/TableWithMonthGroups';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import MutateGate from '../../components/Common/MutateGate';
import ViewOrEditLink from '../../components/Common/ViewOrEditLink';
import ComprasSiteFilterTabs from '../../components/Common/ComprasSiteFilterTabs';
import { useComprasSiteListParams, useComprasSiteViewState } from '../../utils/comprasPurchaseSite';

const ServiceOrders = () => {
  const { siteView, onSiteViewChange } = useComprasSiteViewState();
  const siteListParams = useComprasSiteListParams(siteView);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [orderToToggle, setOrderToToggle] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedInitialData = useRef(false);
  const previousFilters = useRef({ statusFilter: 'all', selectedSupplier: '', searchTerm: '', dateFrom: '', dateTo: '' });
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      previousFilters.current = { statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo };
      loadSuppliers();
      loadOrders();
    }
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousFilters.current = { statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo };
      return;
    }

    if (hasLoadedInitialData.current) {
      const filtersChanged = 
        previousFilters.current.statusFilter !== statusFilter ||
        previousFilters.current.selectedSupplier !== selectedSupplier ||
        previousFilters.current.searchTerm !== searchTerm ||
        previousFilters.current.dateFrom !== dateFrom ||
        previousFilters.current.dateTo !== dateTo;
      
      if (filtersChanged) {
        previousFilters.current = { statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo };
        loadOrders();
      }
    }
  }, [statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo]);

  useEffect(() => {
    if (hasLoadedInitialData.current) {
      loadOrders();
    }
  }, [siteView]);

  const loadSuppliers = async () => {
    try {
      const response = await supplierService.getSuppliers({ active_only: true });
      if (response.success) {
        setSuppliers(response.data);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (selectedSupplier) params.supplier_id = selectedSupplier;
      if (searchTerm) params.search = searchTerm;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      Object.assign(params, siteListParams);

      const response = await serviceOrderService.getServiceOrders(params);
      
      if (response.success && response.data) {
        setOrders(response.data);
      }
    } catch (error) {
      console.error('Error al cargar órdenes:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar las órdenes de servicio',
        type: 'error'
      });
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleToggleStatusClick = (order) => {
    setOrderToToggle(order);
    setIsToggleModalOpen(true);
  };

  const handleToggleStatusConfirm = async () => {
    if (!orderToToggle) return;
    
    setLoading(true);
    try {
      const response = await serviceOrderService.toggleServiceOrderStatus(orderToToggle.id);
      await loadOrders();
      setIsToggleModalOpen(false);
      setOrderToToggle(null);
      setToast({
        isOpen: true,
        message: response.message || (orderToToggle.status === 'cancelled' 
          ? 'Orden de servicio reactivada exitosamente' 
          : 'Orden de servicio cancelada exitosamente'),
        type: 'success'
      });
    } catch (error) {
      console.error('Error al cambiar estado de orden:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || error.message || 'Error al cambiar el estado de la orden de servicio',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Calcular estadísticas
  const totalOrders = orders.length;
  const issuedOrders = orders.filter(o => o.status === 'issued').length;
  const cancelledOrders = orders.filter(o => o.status === 'cancelled').length;
  const totalAmount = orders.reduce((sum, o) => sum + (o.total_amount || 0), 0);

  // Abrir ventana de impresión
  const handlePrint = (order) => {
    const printUrl = `/service-orders/${order.id}/print`;
    window.open(printUrl, '_blank');
  };

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Órdenes de Servicio</h1>
                <p className="text-gray-600 mt-1">Gestión de órdenes de servicio a proveedores</p>
              </div>
              <MutateGate>
                <Link
                  to="/service-orders/create"
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors inline-block"
                >
                  + Nueva Orden de Servicio
                </Link>
              </MutateGate>
            </div>

            <ComprasSiteFilterTabs siteView={siteView} onSiteViewChange={onSiteViewChange} />

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/compras" className="hover:text-teal-600">Compras</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Órdenes de Servicio</span>
            </nav>

            {/* Filtros, Búsqueda y Rango de Fechas */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                {/* Filtro por Estado */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Estados</option>
                  <option value="issued">Emitidas</option>
                  <option value="cancelled">Canceladas</option>
                </select>

                {/* Búsqueda */}
                <input
                  type="text"
                  placeholder="Buscar por código o proveedor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />

                {/* Fecha Desde */}
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Fecha desde"
                />

                {/* Fecha Hasta */}
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Fecha hasta"
                />
              </div>
            </div>

            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Total</div>
                <div className="text-2xl font-bold text-gray-800">{totalOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Emitidas</div>
                <div className="text-2xl font-bold text-teal-600">{issuedOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Canceladas</div>
                <div className="text-2xl font-bold text-gray-500">{cancelledOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Monto Total</div>
                <div className="text-2xl font-bold text-blue-600">S/ {parseFloat(totalAmount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>

          {/* Tabla de Órdenes */}
          {loadingOrders ? (
            <div className="text-center py-12">
              <div className="text-gray-500">Cargando órdenes de servicio...</div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No se encontraron órdenes de servicio</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <TableWithMonthGroups
                data={orders}
                getDateForGrouping={(o) => o.order_date}
                defaultCollapsed
                columnCount={8}
                emptyMessage="No se encontraron órdenes de servicio"
                headerRow={
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entidad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Orden</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Entrega</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                }
                renderRow={(order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/service-orders/${order.id}/edit`} className="text-sm font-mono text-teal-600 hover:text-teal-900">{order.code}</Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{order.supplier?.name || 'N/A'}</div>
                      {order.supplier?.ruc && <div className="text-xs text-gray-500">RUC: {order.supplier.ruc}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateLocaleLima(order.order_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.expected_delivery_date ? formatDateLocaleLima(order.expected_delivery_date) : '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.items?.length || 0} items</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      S/ {parseFloat(order.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${order.status === 'issued' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {order.status === 'issued' ? 'Emitida' : 'Cancelada'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <ViewOrEditLink
                          to={`/service-orders/${order.id}/edit`}
                          className="text-purple-600 hover:text-purple-900"
                        />
                        <button type="button" onClick={() => handlePrint(order)} className="text-blue-600 hover:text-blue-900" title="Imprimir">
                          <svg className="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                        </button>
                        <MutateGate>
                          <button type="button" onClick={() => handleToggleStatusClick(order)} className={order.status === 'issued' ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"} title={order.status === 'issued' ? 'Cancelar' : 'Reactivar'}>
                            {order.status === 'issued' ? 'Cancelar' : 'Reactivar'}
                          </button>
                        </MutateGate>
                      </div>
                    </td>
                  </tr>
                )}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación de cambio de estado */}
      <ConfirmModal
        isOpen={isToggleModalOpen}
        onClose={() => {
          setIsToggleModalOpen(false);
          setOrderToToggle(null);
        }}
        onConfirm={handleToggleStatusConfirm}
        title={orderToToggle?.status === 'issued' ? 'Cancelar Orden de Servicio' : 'Reactivar Orden de Servicio'}
        message={orderToToggle?.status === 'issued' 
          ? `¿Estás seguro de que deseas cancelar la orden "${orderToToggle?.code}"?`
          : `¿Estás seguro de que deseas reactivar la orden "${orderToToggle?.code}"?`}
        confirmText={orderToToggle?.status === 'issued' ? 'Cancelar Orden' : 'Reactivar Orden'}
        cancelText="No"
        isLoading={loading}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ServiceOrders;


